import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useState } from 'react';
import { Users, Clock, AlertOctagon, TrendingUp, RefreshCw, Banknote, Activity } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import ExplainVarianceBubble from '../../../components/reports/ExplainVarianceBubble';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmtGBP2 = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const STATUS = {
  good: { dot: 'bg-signal-ok-line', cls: 'text-signal-ok bg-signal-ok-tint border-signal-ok-line', label: 'On target' },
  warn: { dot: 'bg-signal-warn-line', cls: 'text-signal-warn bg-signal-warn-tint border-signal-warn-line', label: 'Watch' },
  bad:  { dot: 'bg-signal-stop-line', cls: 'text-signal-stop bg-signal-stop-tint border-signal-stop-line', label: 'Critical' },
  neutral: { dot: 'bg-ink-faint', cls: 'text-ink-soft bg-surface-2 border-hairline', label: 'Live' },
};

const Hero = ({ icon: Icon, label, value, sub, status = 'neutral', testid, kpi, range, siteLabel, sharedContext, seeMoreTo }) => {
  const meta = STATUS[status];
  return (
    <div className="bg-white border border-hairline rounded-lg p-5 relative group hover:border-ink-faint hover:shadow-[0_8px_30px_-12px_rgba(32,27,95,0.18)] transition-all flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-ink-faint" />
          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">{label}</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`}>
          <span className={`w-1 h-1 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
      </div>
      <div className="font-mono-display text-3xl md:text-4xl font-medium text-ink">{value}</div>
      {sub && <p className="text-[12.5px] text-ink-faint mt-1">{sub}</p>}
      {kpi && <ExplainVarianceBubble kpi={kpi} range={range} siteLabel={siteLabel} context={sharedContext} />}
      {seeMoreTo && (
        <div className="mt-3 pt-3 border-t border-hairline flex justify-end">
          <SeeMoreLink to={seeMoreTo} what={label} testId={`${testid}-see-more`} />
        </div>
      )}
    </div>
  );
};

const LabourIntelligencePage = () => {
  const [sites, setSites] = useState([]);
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const siteLabel = useMemo(() => sites.find((s) => s.id === site)?.name || site, [sites, site]);

  useEffect(() => { reportsIntelApi.listSites().then((r) => setSites(r.data.sites || [])).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await reportsIntelApi.getLabourIntel(site); setData(res.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [site]);

  const sharedContext = data ? {
    labour_pct: data.totals.labour_pct,
    labour_pct_target: data.totals.labour_pct_target,
    cost_per_cover: data.totals.cost_per_cover,
    overtime_count: data.totals.overtime_count,
    splh: data.totals.splh,
    site_id: site,
  } : null;

  return (
    <ReportsLayout>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <SitePicker sites={sites} value={site} onChange={setSite} allowAll allValue="" triggerClassName="h-9 w-[230px] bg-white border-hairline" testId="labour-site-select" />
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => { setRefreshing(true); load().finally(() => setRefreshing(false)); }} data-testid="labour-refresh-btn">
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />)}
        </div>
      ) : data.is_empty ? (
        <div className="bg-white border border-hairline rounded-lg p-12 text-center" data-testid="labour-empty">
          <Users size={28} className="text-ink-faint mx-auto mb-3" />
          <h3 className="text-[14px] font-semibold text-ink">No labour data yet</h3>
          <p className="text-[12px] text-ink-soft mt-1">No staff shifts scheduled in the last 7 days for this site.</p>
        </div>
      ) : (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
            <Hero
              testid="labour-hero-labour-pct"
              icon={Activity}
              label="Labour %"
              value={`${data.totals.labour_pct}%`}
              sub={`Target ${data.totals.labour_pct_target}% · ${fmtGBP(data.totals.wage_spend)} wage spend`}
              status={data.totals.labour_pct > data.totals.labour_pct_target + 3 ? 'bad' : data.totals.labour_pct > data.totals.labour_pct_target ? 'warn' : 'good'}
              kpi={{ key: 'labour_pct', label: 'Labour %', value: data.totals.labour_pct, target: data.totals.labour_pct_target, format: 'percent' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
            <Hero
              testid="labour-hero-cost-per-cover"
              icon={Banknote}
              label="Cost per cover"
              value={fmtGBP2(data.totals.cost_per_cover)}
              sub={`${data.totals.covers} covers this week`}
              status="neutral"
              kpi={{ key: 'cost_per_cover', label: 'Cost per cover', value: data.totals.cost_per_cover, format: 'currency' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
            <Hero
              testid="labour-hero-overtime"
              icon={AlertOctagon}
              label="Overtime risk"
              value={data.totals.overtime_count}
              sub={`${data.totals.late_count} late · ${data.totals.clocked_in_now} clocked in now`}
              status={data.totals.overtime_count > 3 ? 'bad' : data.totals.overtime_count > 1 ? 'warn' : 'good'}
              kpi={{ key: 'overtime_risk', label: 'Overtime risk count', value: data.totals.overtime_count, target: 0, format: 'int' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
            <Hero
              testid="labour-splh"
              icon={TrendingUp}
              label="Sales / labour hour"
              value={fmtGBP2(data.totals.splh)}
              sub={`${data.totals.planned_hours}h scheduled · adherence ${data.totals.adherence_pct}%`}
              status="neutral"
              kpi={{ key: 'splh', label: 'Sales per labour hour', value: data.totals.splh, format: 'currency' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
          </div>

          {/* Departments + risk lists */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">
            <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="labour-departments">
              <div className="px-5 py-4 border-b border-hairline">
                <h3 className="text-[13px] font-semibold text-ink">Department performance</h3>
                <p className="text-[12.5px] text-ink-soft">Adherence + wage spend</p>
              </div>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-ink-faint border-b border-hairline">
                    <th className="text-left font-semibold px-5 py-2">Department</th>
                    <th className="text-right font-semibold px-3 py-2">Head-count</th>
                    <th className="text-right font-semibold px-3 py-2">Hrs (act/plan)</th>
                    <th className="text-right font-semibold px-3 py-2">Adherence</th>
                    {/* On-costs are in this figure where the business counts them (#293), so "wages" named
                        it wrongly. */}
                    <th className="text-right font-semibold px-5 py-2">Labour cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.departments.map((d) => (
                    <tr key={d.key} className="border-b border-hairline last:border-0" data-testid="labour-dept-row" data-dept={d.key}>
                      <td className="px-5 py-2.5 text-ink font-semibold">{d.label}</td>
                      <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{d.headcount}</td>
                      <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{d.actual_hours}<span className="text-ink-faint">/{d.planned_hours}</span></td>
                      <td className={`text-right px-3 py-2.5 font-mono-display font-semibold ${d.adherence_pct >= 95 && d.adherence_pct <= 105 ? 'text-signal-ok' : d.adherence_pct < 90 || d.adherence_pct > 110 ? 'text-signal-stop' : 'text-signal-warn'}`}>{d.adherence_pct}%</td>
                      <td className="text-right px-5 py-2.5 font-mono-display text-ink">{fmtGBP(d.wage_spend)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-hairline bg-surface-2/60">
                    <td className="px-5 py-2.5 text-ink font-semibold">Total</td>
                    <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{data.totals.headcount}</td>
                    <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{data.totals.actual_hours}<span className="text-ink-faint">/{data.totals.planned_hours}</span></td>
                    <td className="text-right px-3 py-2.5 font-mono-display font-semibold text-ink-soft">{data.totals.adherence_pct}%</td>
                    <td className="text-right px-5 py-2.5 font-mono-display text-ink font-semibold" data-testid="labour-cost-total">{fmtGBP(data.totals.wage_spend)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-5">
              <div className="bg-white border border-hairline rounded-lg p-5" data-testid="labour-overtime-list">
                <h3 className="text-[13px] font-semibold text-ink flex items-center gap-2"><AlertOctagon size={14} className="text-signal-stop" /> Overtime risk</h3>
                <p className="text-[12.5px] text-ink-soft mb-3">Staff exceeding 40 hours this week</p>
                {data.overtime_staff.length === 0 ? (
                  <p className="text-[12px] text-ink-faint italic">No overtime risk this week.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.overtime_staff.map((s) => (
                      <li key={s.name} className="flex items-center justify-between text-[12px]">
                        <span><span className="font-semibold text-ink">{s.name}</span><span className="text-ink-faint ml-1.5 capitalize">· {s.role}</span></span>
                        <span className="font-mono-display text-signal-stop font-semibold">+{s.over_by}h</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white border border-hairline rounded-lg p-5" data-testid="labour-late-list">
                <h3 className="text-[13px] font-semibold text-ink flex items-center gap-2"><Clock size={14} className="text-signal-warn" /> Late arrivals this week</h3>
                {data.late_staff.length === 0 ? (
                  <p className="text-[12px] text-ink-faint italic mt-2">No lateness recorded.</p>
                ) : (
                  <ul className="space-y-1.5 mt-3">
                    {data.late_staff.map((s) => (
                      <li key={s.name} className="flex items-center justify-between text-[12px]">
                        <span><span className="font-semibold text-ink">{s.name}</span><span className="text-ink-faint ml-1.5">· {s.role}</span></span>
                        <span className="font-mono-display text-signal-warn font-semibold">{s.late_minutes}m</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </ReportsLayout>
  );
};

export default LabourIntelligencePage;
